import React, { useMemo, useRef } from 'react';
import * as R from 'ramda';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, LinearProgress, Theme, Typography } from '@material-ui/core';
import * as icons from '@material-ui/icons';

import { SubmitButton } from 'src/components';
import {
  Table,
  MetricColumnRenderer,
  renderTierColumn,
  renderValueColumn,
  renderToggleColumn,
} from './';

import { MetricValue, TableExportType } from 'src/graphql';
import {
  DateFormatPatterns,
  insertDelimitersAfter,
  detectArrowType,
  formatDate,
  detectSharedMetric,
} from 'src/utils';
import {
  CELL_TYPES,
  MetricCode,
  ScoreMetricItem,
  ScoreMetricsTableDataStructureRow,
} from 'src/types';
import { TABLE_DELIMITER } from 'src/constants';
import { useTableExport, useToggleMetricsContext } from 'src/hooks';

import cellRenderClasses from 'src/shared/metrics/TableUnderwritingReport/CellRenderer.module.css';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    overflow: 'hidden',
  },
  header: {
    height: theme.spacing(8),
    padding: theme.spacing(2),
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  form: {
    padding: theme.spacing(2),
  },
  right: {
    marginLeft: 'auto',
  },
}));

interface LayoutProps {
  filters: React.ReactNode;
  tableData: { data: any[]; dates: string[] };
  loading: boolean;
}

export const Layout: React.FC<LayoutProps> = ({ filters, tableData, loading }) => {
  const classes = useStyles();
  const { areTogglesExpanded } = useToggleMetricsContext();

  const prevMetricValue = useRef<MetricValue | undefined>();

  const dates: string[] = tableData?.dates;
  const scoreMetricItems: ScoreMetricItem[] = R.pathOr([], ['data'], tableData);

  const arrowTypes = dates?.map((date, idx) => detectArrowType(dates, scoreMetricItems, date, idx));

  const metricsWithDelimiters = React.useMemo(() => {
    const delimiterAfterCodes = [MetricCode.NetCashBurnOfGrossProfit];

    const compare = (metricItem: ScoreMetricItem, metricCode: MetricCode) => {
      return R.propEq('code', metricCode)(metricItem);
    };

    return insertDelimitersAfter<ScoreMetricItem>(
      delimiterAfterCodes,
      scoreMetricItems as ScoreMetricItem[],
      compare,
    );
  }, [scoreMetricItems]);

  const colSpan = areTogglesExpanded ? 3 : 2;

  const columns = useMemo(
    () => [
      {
        dataIndex: 'name',
        exportData: { title: ' ', type: CELL_TYPES.text, key: 'name' },
        render: MetricColumnRenderer,
        width: 300,
        fixed: 'left',
      },
      ...dates
        .map((date, dateIdx) => [
          {
            title: formatDate(
              date,
              DateFormatPatterns.shortDateWithSlash,
              DateFormatPatterns.shortDateWithDash,
            ),
            exportData: { title: date, type: CELL_TYPES.text, key: date },
            colSpan,
            width: 110,
            dataIndex: date,
            render: (
              metricValue: MetricValue,
              row: ScoreMetricsTableDataStructureRow,
              idx: number,
            ) => {
              if (row === TABLE_DELIMITER) {
                return <div className={cellRenderClasses.emptyCell} />;
              }

              return renderValueColumn({
                metricValue,
                colSpan,
                idx,
                date,
                prevMetricValue,
                metricCode: (row as ScoreMetricItem).code,
                metricName: (row as ScoreMetricItem).name,
                arrowType: arrowTypes[dateIdx],
              });
            },
          },
          {
            colSpan: 0,
            width: 35,
            dataIndex: date,
            render: (value: MetricValue, row: ScoreMetricsTableDataStructureRow, idx: number) => {
              const isSharedMetric = detectSharedMetric(row);

              if (row === TABLE_DELIMITER || isSharedMetric) {
                return <div className={cellRenderClasses.emptyCell} />;
              }

              return renderTierColumn(value, row, idx);
            },
          },
          ...(areTogglesExpanded
            ? [
                {
                  colSpan: 0,
                  width: 75,
                  dataIndex: date,
                  render: (
                    metricValue: MetricValue,
                    row: ScoreMetricsTableDataStructureRow,
                    idx: number,
                  ) => {
                    const isSharedMetric = detectSharedMetric(row);
                    const isEmptyCell = row === TABLE_DELIMITER || isSharedMetric;

                    if (isEmptyCell) {
                      return <div className={cellRenderClasses.emptyCell} />;
                    }

                    return renderToggleColumn(metricValue, idx);
                  },
                },
              ]
            : []),
        ])
        .flat(),
    ],
    [dates, colSpan, areTogglesExpanded, arrowTypes],
  );

  const [onExport, { loading: isExporting }] = useTableExport(TableExportType.ScoreAndMetrics, {
    rows: scoreMetricItems,
    columns,
  });

  return (
    <Grid className={classes.root} container direction="column" wrap="nowrap">
      <Grid className={classes.header} item alignItems="center">
        <Typography variant="subtitle1">SaaSScore &amp; Key Metrics</Typography>

        <Grid className={classes.right} item>
          <SubmitButton
            onClick={onExport}
            color="secondary"
            variant="outlined"
            size="large"
            startIcon={<icons.GetApp />}
            loading={isExporting}
          >
            Export
          </SubmitButton>
        </Grid>
      </Grid>

      {loading ? (
        <LinearProgress />
      ) : (
        <>
          <Grid className={classes.form} item container alignItems="center" spacing={2}>
            <Grid item container alignItems="center" xs={8}>
              {filters}
            </Grid>
          </Grid>
          <Table data={metricsWithDelimiters} dates={dates} columns={columns} />
        </>
      )}
    </Grid>
  );
};
